import { useState, useEffect } from 'react';
import { useCookieConsent } from './useCookieConsent';

export type FontSize = 'small' | 'medium' | 'large' | 'extra-large';
export type Contrast = 'normal' | 'high';

export interface AccessibilitySettings {
  fontSize: FontSize;
  contrast: Contrast;
  reducedMotion: boolean;
  highReadability: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 'medium',
  contrast: 'normal',
  reducedMotion: false,
  highReadability: false,
};

export const useAccessibility = () => {
  const { functional } = useCookieConsent();
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);

  // Load settings from localStorage if functional cookies are enabled
  useEffect(() => {
    if (functional) {
      const savedSettings = localStorage.getItem('accessibility-settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setSettings({ ...defaultSettings, ...parsed });
        } catch (error) {
          console.error('Error parsing accessibility settings:', error);
        }
      }
    }
  }, [functional]);

  // Apply accessibility settings to document
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Font size classes
    root.classList.remove('text-sm', 'text-base', 'text-lg', 'text-xl');
    switch (settings.fontSize) {
      case 'small':
        root.classList.add('text-sm');
        break;
      case 'medium':
        root.classList.add('text-base');
        break;
      case 'large':
        root.classList.add('text-lg');
        break;
      case 'extra-large':
        root.classList.add('text-xl');
        break;
    }
    
    // Contrast
    if (settings.contrast === 'high') {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Reduced motion
    if (settings.reducedMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
    
    // High readability
    if (settings.highReadability) {
      root.classList.add('high-readability');
    } else {
      root.classList.remove('high-readability');
    }
  }, [settings]);

  const updateSettings = (newSettings: Partial<AccessibilitySettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    
    // Only save to localStorage if functional cookies are enabled
    if (functional) {
      localStorage.setItem('accessibility-settings', JSON.stringify(updatedSettings));
    }
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    if (functional) {
      localStorage.removeItem('accessibility-settings');
    }
  };

  return {
    settings,
    updateSettings,
    resetSettings,
    canPersist: functional,
  };
};